import React, { ReactNode, useState, useEffect } from 'react'
import Button from '@renderer/components/Button'
import { useTranslation } from 'react-i18next'
import { useSessionLayout } from '@renderer/hooks'

interface PinModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (pin: string) => void
  title: string
  subtitle?: string | ReactNode
}

const PinModal: React.FC<PinModalProps> = ({ isOpen, onClose, onSubmit, title, subtitle }) => {
  const { t } = useTranslation()
  const { isCompact } = useSessionLayout()
  const [pin, setPin] = useState('')

  const handleNumberClick = (num: string): void => {
    if (num === 'C') {
      setPin('')
    } else if (num === '⌫') {
      setPin((prev) => prev.slice(0, -1))
    } else {
      if (pin.length < 6) {
        setPin((prev) => prev + num)
      }
    }
  }

  const handleSubmit = (): void => {
    if (pin.length === 6) {
      onSubmit(pin)
      setPin('')
    }
  }

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      // Handle number keys (0-9)
      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault()
        setPin((prev) => {
          if (prev.length < 6) {
            return prev + event.key
          }
          return prev
        })
      }
      // Handle backspace or delete
      else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()
        setPin((prev) => prev.slice(0, -1))
      }
      // Handle escape to close
      else if (event.key === 'Escape') {
        event.preventDefault()
        setPin('')
        onClose()
      }
      // Handle enter to submit
      else if (event.key === 'Enter') {
        event.preventDefault()
        setPin((currentPin) => {
          if (currentPin.length === 6) {
            onSubmit(currentPin)
            return ''
          }
          return currentPin
        })
      }
      // Handle 'c' or 'C' to clear
      else if (event.key.toLowerCase() === 'c') {
        event.preventDefault()
        setPin('')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose, onSubmit])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/50 overflow-y-auto overscroll-contain p-2 sm:p-4">
      <div
        className={`m-auto w-full bg-white rounded-lg ${
          isCompact ? 'max-w-xs p-4' : 'max-w-sm p-8'
        }`}
      >
        <h2
          className={`font-bold mb-2 text-center text-carbon ${
            isCompact ? 'text-lg' : 'text-2xl'
          }`}
        >
          {title}
        </h2>
        <div
          className={`text-gray-500 mb-4 text-center ${isCompact ? 'text-xs' : 'text-sm'}`}
        >
          {subtitle || t('settings.pinModal.change.subtitle')}
        </div>
        <div className={isCompact ? 'mb-3' : 'mb-6'}>
          <div
            className={`flex justify-center mb-4 ${isCompact ? 'gap-2' : 'gap-3 mb-8'}`}
          >
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`border-2 border-gray-300 rounded-md flex items-center justify-center ${
                  isCompact ? 'w-8 h-8 text-lg' : 'w-12 h-12 text-2xl'
                }`}
              >
                {pin[i] ? '•' : ''}
              </div>
            ))}
          </div>
          {/* data-no-log: each key's label is a PIN digit, so the click logger
              must never record these (see utils/clickLog). */}
          <div data-no-log className={`grid grid-cols-3 ${isCompact ? 'gap-1.5' : 'gap-3'}`}>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleNumberClick(num)}
                className={`bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors ${
                  isCompact ? 'py-2 px-1 text-base' : 'p-4 text-xl'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
        <div className={`flex justify-end gap-2 ${isCompact ? 'flex-wrap' : 'gap-3'}`}>
          <Button
            text={t('common.cancel')}
            variant="secondary"
            onClick={() => {
              setPin('')
              onClose()
            }}
            className={
              isCompact ? '!px-4 !py-1.5 !text-sm' : '!px-6 !py-2 !text-base'
            }
          />
          <Button
            text={t('common.save')}
            onClick={handleSubmit}
            className={
              isCompact ? '!px-4 !py-1.5 !text-sm' : '!px-6 !py-2 !text-base'
            }
            disabled={pin.length !== 6}
          />
        </div>
      </div>
    </div>
  )
}

export default PinModal
